import { useSyncExternalStore } from 'react';

import { useTranslation } from 'react-i18next';
import { Moon, Sun } from 'lucide-react';

import sunIcon from '@/assets/icon-toggle/sun.png';
import nightIcon from '@/assets/icon-toggle/night.png';
import { Button } from '@/components/ui/button';
import { useThemePreference } from '@/hooks/useThemePreference';
import { cn } from '@/lib/utils';

// Centralized theme storage key - exported for use in ThemeProvider
export const THEME_STORAGE_KEY = 'app-theme';

const noop = () => {};
const emptySubscribe = () => noop;
const getSnapshot = () => true;
const getServerSnapshot = () => false;

function useIsMounted() {
  return useSyncExternalStore(emptySubscribe, getSnapshot, getServerSnapshot);
}

export function ThemeToggle({
  className,
  collapsed,
}: {
  className?: string;
  collapsed?: boolean;
}) {
  const { updateTheme, currentTheme, isLoading } = useThemePreference();
  const { t } = useTranslation();
  const mounted = useIsMounted();

  if (!mounted || isLoading) {
    return <div className="bg-muted h-6 w-12 rounded-full" />; // Skeleton
  }

  const isDark = currentTheme === 'dark';
  const toggleTheme = () => updateTheme(isDark ? 'light' : 'dark');

  // Collapsed mode - icon button only
  if (collapsed) {
    return (
      <Button
        variant="ghost"
        size="icon"
        onClick={toggleTheme}
        className={cn('h-8 w-8', className)}
        title={t('common.displayMode', 'Chế độ hiển thị')}
      >
        {isDark ? (
          <img
            src={nightIcon}
            alt="Dark mode"
            className="h-5 w-5"
          />
        ) : (
          <img
            src={sunIcon}
            alt="Light mode"
            className="h-5 w-5"
          />
        )}
      </Button>
    );
  }

  // Expanded mode - label + custom toggle switch with icons
  return (
    <div
      className={cn(
        'flex w-full items-center justify-between px-2 py-1',
        className,
      )}
    >
      <span className="text-muted-foreground text-sm font-medium">
        {t('common.displayMode', 'Chế độ hiển thị')}
      </span>

      <button
        type="button"
        role="switch"
        aria-checked={!isDark}
        onClick={toggleTheme}
        className={cn(
          'relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out',
          'focus-visible:ring-ring focus-visible:ring-offset-background focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
          !isDark ? 'bg-primary' : 'bg-input',
        )}
      >
        <span
          className={cn(
            'bg-background pointer-events-none flex h-6 w-6 items-center justify-center rounded-full shadow-lg ring-0 transition-transform duration-200 ease-in-out',
            !isDark ? 'translate-x-5' : 'translate-x-0',
          )}
        >
          {!isDark ? (
            <Sun className="text-warning h-4 w-4" />
          ) : (
            <Moon className="text-muted-foreground h-4 w-4" />
          )}
        </span>
      </button>
    </div>
  );
}
